/**
 * OCPI Party Code Generator
 *
 * Utilities for generating OCPI-compliant party identifiers
 * based on operator information.
 */

/**
 * Currency to ISO 3166-1 alpha-2 country code mapping
 * Maps common currencies to their primary country codes
 */
const CURRENCY_TO_COUNTRY: Record<string, string> = {
  // Europe
  EUR: 'DE', // Default to Germany for Euro (can be overridden)
  GBP: 'GB',
  CHF: 'CH',
  SEK: 'SE',
  NOK: 'NO',
  DKK: 'DK',
  PLN: 'PL',
  CZK: 'CZ',
  HUF: 'HU',
  RON: 'RO',
  BGN: 'BG',
  HRK: 'HR',
  ISK: 'IS',
  RUB: 'RU',
  UAH: 'UA',
  TRY: 'TR',

  // Americas
  USD: 'US',
  CAD: 'CA',
  MXN: 'MX',
  BRL: 'BR',
  ARS: 'AR',
  CLP: 'CL',
  COP: 'CO',
  PEN: 'PE',

  // Asia Pacific
  JPY: 'JP',
  CNY: 'CN',
  KRW: 'KR',
  INR: 'IN',
  SGD: 'SG',
  HKD: 'HK',
  TWD: 'TW',
  THB: 'TH',
  MYR: 'MY',
  IDR: 'ID',
  PHP: 'PH',
  VND: 'VN',
  AUD: 'AU',
  NZD: 'NZ',

  // Middle East & Africa
  ILS: 'IL',
  AED: 'AE',
  SAR: 'SA',
  QAR: 'QA',
  KWD: 'KW',
  BHD: 'BH',
  OMR: 'OM',
  EGP: 'EG',
  ZAR: 'ZA',
  NGN: 'NG',
  KES: 'KE',
};

/**
 * Get country code from currency code
 *
 * @param currency - ISO 4217 currency code (e.g., "EUR", "USD", "ILS")
 * @returns ISO 3166-1 alpha-2 country code or empty string if not found
 */
export function getCountryCodeFromCurrency(currency: string | undefined | null): string {
  if (!currency) return '';

  const upperCurrency = currency.toUpperCase().trim();
  return CURRENCY_TO_COUNTRY[upperCurrency] || '';
}

/**
 * Generate a 3-letter party ID from operator name
 *
 * Algorithm:
 * 1. If name has multiple words, take first letter of each word (up to 3)
 * 2. If name is single word or acronym doesn't reach 3 chars, take first 3 consonants
 * 3. If still not enough, pad with first letters
 * 4. Always returns uppercase 3-letter code
 *
 * Examples:
 * - "IP Innovative" -> "IPI"
 * - "Nayax Energy" -> "NAE" or "NYE"
 * - "Tesla" -> "TSL"
 * - "ABC Company" -> "ABC"
 *
 * @param operatorName - The operator's display name
 * @returns A 3-letter party ID suggestion
 */
export function generatePartyIdFromName(operatorName: string | undefined | null): string {
  if (!operatorName) return '';

  // Clean the name: remove special characters, extra spaces
  const cleanName = operatorName
    .toUpperCase()
    .replace(/[^A-Z0-9\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  if (!cleanName) return '';

  const words = cleanName.split(' ').filter(w => w.length > 0);

  // Strategy 1: Take first letter of each word (for multi-word names)
  if (words.length >= 3) {
    return words.slice(0, 3).map(w => w[0]).join('');
  }

  if (words.length === 2) {
    // Two words: first letter of first, first two of second (or vice versa)
    const first = words[0];
    const second = words[1];

    // Try: first letter of first word + first two letters of second word
    if (second.length >= 2) {
      return first[0] + second.slice(0, 2);
    }
    // Fallback: two letters from first + one from second
    return first.slice(0, 2) + second[0];
  }

  // Single word: extract meaningful letters
  const singleWord = words[0];

  // If it's already 3 letters or less, use as is
  if (singleWord.length <= 3) {
    return singleWord.padEnd(3, 'X');
  }

  // Strategy 2: Take consonants (skip vowels for abbreviation)
  const vowels = 'AEIOU';
  const consonants = singleWord.split('').filter(c => !vowels.includes(c));

  if (consonants.length >= 3) {
    // First letter + first two consonants (if first is vowel) or first 3 consonants
    if (vowels.includes(singleWord[0])) {
      return singleWord[0] + consonants.slice(0, 2).join('');
    }
    return consonants.slice(0, 3).join('');
  }

  // Strategy 3: Just take first 3 letters
  return singleWord.slice(0, 3);
}

/**
 * Validate if a string is a valid OCPI party ID
 *
 * @param partyId - The party ID to validate
 * @returns true if valid (exactly 3 uppercase letters)
 */
export function isValidPartyId(partyId: string): boolean {
  return /^[A-Z]{3}$/.test(partyId);
}

/**
 * Validate if a string is a valid OCPI country code
 *
 * @param countryCode - The country code to validate
 * @returns true if valid (exactly 2 uppercase letters)
 */
export function isValidCountryCode(countryCode: string): boolean {
  return /^[A-Z]{2}$/.test(countryCode);
}

/**
 * Generate suggested OCPI party information from operator details
 *
 * @param operatorName - The operator's display name
 * @param currency - The operator's currency code
 * @returns Object with suggested partyId and countryCode
 */
export function generateOcpiSuggestions(
  operatorName: string | undefined | null,
  currency: string | undefined | null
): { partyId: string; countryCode: string } {
  return {
    partyId: generatePartyIdFromName(operatorName),
    countryCode: getCountryCodeFromCurrency(currency),
  };
}
